import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext";
import "./LookupPatient.css";
import "../PracticeManagement/PatientManagement/PatientProgramAnalytics.css";
import practiceService from "../../services/practiceService";
import systemCaregiverService from "../../services/systemCaregiverService";
import { formatDateOnlyUs, getTodayDateOnlyLocal } from "../../utils/dateOnlyUtils";
import { normalizePhoneDigits } from "../../utils/phoneUtils";
import PhoneNumberInput from "../../components/Common/PhoneNumberInput/PhoneNumberInput";
import AppDateInput from "../../components/Common/AppDateInput/AppDateInput";
import ListScreenPaginationBar from "../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar";
import StatusBadge from "../../components/Common/StatusBadge/StatusBadge";
import Table from "../../components/Common/Table/Table";
import "../../components/Common/ListScreenPageLayout/listScreenPage.css";
import { patientStatusLabel, getPatientStatusSortOrder, resolvePatientStatusRaw } from "../../utils/patientStatusUtils";
import { formatChronicDiseaseNames } from "../../utils/patientBillingUtils";
import {
  buildPatientDetailsPath,
  resolvePatientPracticeId,
  resolvePatientRecordId
} from "../../utils/patientRouteUtils";
import {
  fetchAssignedPracticesForSystemCaregiver,
  isSystemCaregiverRole
} from "../../utils/systemCaregiverPracticeUtils";
import { matchesWordStartAny } from "../../utils/searchMatch";
import { getPracticeProgramFlags } from "../../utils/practiceProgramsUtils";
import { DEVICE_SERIAL_NUMBER_LENGTH, normalizeDeviceSerialInput } from "../../utils/meterMappingUtils";
import { formatPhoneForDisplay } from "../../utils/phoneUtils";
import EmailModal from "../../components/EmailModal/EmailModal";
import PrintModal from "../../components/PrintModal/PrintModal";
import { sendReportEmail } from "../../utils/reportEmailSend";
import { exportColoredTablePdf } from "../../utils/coloredTablePdfExport";
import {
  checkBPValues,
  checkBGValue,
  checkWeightValue,
  getMeasurementAlertStatus,
  kgToLb
} from "../../utils/measurementUtils";
import pulseIcon from "../../assets/pulse.png";
import PulseIcon from "../../components/Common/PulseIcon/PulseIcon";

const LIST_VITAL_TARGETS = {
  bloodPressure: {
    systolicMin: 100,
    systolicMax: 140,
    diastolicMin: 60,
    diastolicMax: 90
  },
  bloodGlucose: {
    minimum: 60,
    maximum: 110
  },
  weight: {
    weightMin: 66,
    weightMax: 220
  },
  pulse: {
    min: 60,
    max: 100
  }
};

function vitalAlertClass(status) {
  if (status === "high") return "alert-high";
  if (status === "low") return "alert-low";
  if (status === "normal") return "alert-normal";
  return "";
}

/** Match Patient Management / UI measurement colors for export & print. */
const VITAL_STATUS_COLORS = {
  high: "#d32f2f",
  low: "#f57c00",
  normal: "#15803d"
};

/** UI table header blue (--ev-table-header / --ev-palette-700). */
const LOOKUP_TABLE_HEADER_BG = "#0077b6";

function vitalStatusLabel(status) {
  if (status === "high") return "High";
  if (status === "low") return "Low";
  if (status === "normal") return "Normal";
  return "";
}

function makeVitalExportCell(raw, status) {
  const num = raw != null && raw !== "N/A" && raw !== "" ? Number(raw) : null;
  if (num == null || Number.isNaN(num) || num <= 0) {
    return { text: "", csv: "", color: null, status: null };
  }
  const text = String(Math.round(num));
  const label = vitalStatusLabel(status);
  return {
    text,
    csv: label ? `${text} (${label})` : text,
    color: VITAL_STATUS_COLORS[status] || VITAL_STATUS_COLORS.normal,
    status: status || null
  };
}

function makeWeightExportCell(patient, status) {
  const kg = patient.last_weight ?? patient.weight;
  const val = kg;
  if (val == null || val === "" || val === "N/A" || Number(val) <= 0 || Number.isNaN(Number(val))) {
    return { text: "", csv: "", color: null, status: null };
  }
  const text = String(val);
  const label = vitalStatusLabel(status);
  return {
    text,
    csv: label ? `${text} (${label})` : text,
    color: VITAL_STATUS_COLORS[status] || VITAL_STATUS_COLORS.normal,
    status: status || null
  };
}

function plainExportCell(value) {
  const text = value == null ? "" : String(value);
  return { text, csv: text, color: null, status: null };
}

function renderVitalValue(value, status) {
  if (
    value == null ||
    value === "" ||
    value === "N/A" ||
    value === 0 ||
    value === "0" ||
    Number(value) <= 0 ||
    Number.isNaN(Number(value))
  ) {
    return "N/A";
  }
  const cls = vitalAlertClass(status);
  if (!cls) return value;
  return <span className={`lookup-patient-vital-value ${cls}`}>{value}</span>;
}

function renderPulseValue(value, status) {
  if (
    value == null ||
    value === "" ||
    value === "N/A" ||
    value === 0 ||
    value === "0" ||
    Number(value) <= 0 ||
    Number.isNaN(Number(value))
  ) {
    return "N/A";
  }
  const cls = vitalAlertClass(status);
  const isAbnormal = status === "high" || status === "low";
  return (
    <span className={`lookup-patient-pulse-with-icon lookup-patient-vital-value ${cls}`}>
      {value}
      <PulseIcon isAbnormal={isAbnormal} className="lookup-patient-pulse-icon" />
    </span>
  );
}

function getPatientListVitalStatuses(patient) {
  const sys = patient?.last_systolic ?? patient?.systolic;
  const dia = patient?.last_diastolic ?? patient?.diastolic;
  const pulse = patient?.last_pulse ?? patient?.pulse;
  const glucose = patient?.last_glucose ?? patient?.glucose;
  const weightKg = patient?.last_weight ?? patient?.weight;

  const sysNum = sys != null && sys !== "N/A" ? Number(sys) : null;
  const diaNum = dia != null && dia !== "N/A" ? Number(dia) : null;

  const bpTarget = {
    systolicMin: patient?.target_sys_min ?? LIST_VITAL_TARGETS.bloodPressure.systolicMin,
    systolicMax: patient?.target_sys_max ?? LIST_VITAL_TARGETS.bloodPressure.systolicMax,
    diastolicMin: patient?.target_dia_min ?? LIST_VITAL_TARGETS.bloodPressure.diastolicMin,
    diastolicMax: patient?.target_dia_max ?? LIST_VITAL_TARGETS.bloodPressure.diastolicMax,
  };

  const bpStatus = checkBPValues(
    {
      systolic_pressure: sysNum != null && !Number.isNaN(sysNum) && sysNum > 0 ? sysNum : null,
      diastolic_pressure: diaNum != null && !Number.isNaN(diaNum) && diaNum > 0 ? diaNum : null
    },
    bpTarget
  );

  const bgTarget = {
    minimum: patient?.target_bg_min ?? LIST_VITAL_TARGETS.bloodGlucose.minimum,
    maximum: patient?.target_bg_max ?? LIST_VITAL_TARGETS.bloodGlucose.maximum,
  };

  const weightTarget = {
    weightMin: patient?.target_weight_min ?? LIST_VITAL_TARGETS.weight.weightMin,
    weightMax: patient?.target_weight_max ?? LIST_VITAL_TARGETS.weight.weightMax,
  };

  const pulseNum = pulse != null && pulse !== "N/A" ? Number(pulse) : null;
  const pulseMin = patient?.target_pulse_min ?? patient?.pulse_min ?? LIST_VITAL_TARGETS.pulse.min;
  const pulseMax = patient?.target_pulse_max ?? patient?.pulse_max ?? LIST_VITAL_TARGETS.pulse.max;
  const pulseStatus =
    pulseNum == null || Number.isNaN(pulseNum) || pulseNum <= 0
      ? "normal"
      : getMeasurementAlertStatus(pulseNum, pulseMin, pulseMax);

  const glucoseNum = glucose != null && glucose !== "N/A" ? Number(glucose) : null;
  const glucoseStatus =
    glucoseNum == null || Number.isNaN(glucoseNum) || glucoseNum <= 0
      ? "normal"
      : checkBGValue(glucoseNum, bgTarget);

  const weightNum = weightKg != null && weightKg !== "N/A" ? Number(weightKg) : null;
  const weightStatus =
    weightNum == null || Number.isNaN(weightNum) || weightNum <= 0
      ? "normal"
      : checkWeightValue(weightNum, weightTarget);

  return {
    sysStatus: bpStatus.sysStatus,
    diaStatus: bpStatus.diaStatus,
    pulseStatus,
    glucoseStatus,
    weightStatus
  };
}

const PROGRAM_BOTH = "both";
const PROGRAM_RPM = "rpm";
const PROGRAM_CCM = "ccm";
const PROGRAM_PCM = "pcm";
const PROGRAM_SELECT = "";

const DOB_OPERATORS = [
  { value: "", label: "-- Select --" },
  { value: "on", label: "On" },
  { value: "onOrBefore", label: "On or before" },
  { value: "onOrAfter", label: "On or after" },
  { value: "between", label: "Between" }
];

const emptyLookupFilters = () => ({
  lastName: "",
  firstName: "",
  practice: "",
  provider: "",
  caregiver: "",
  systemCaregiver: "",
  status: "",
  vitals: [],
  serialNumber: "",
  phone: "",
  dobOperator: "",
  dobFrom: "",
  dobTo: "",
  rpmStartFrom: "",
  rpmStartTo: "",
  programEnrolled: PROGRAM_SELECT
});

/** Fields that live in the "More filters" panel (the toolbar holds practice, provider, status, program). */
const emptyPanelFilters = () => ({
  lastName: "",
  firstName: "",
  phone: "",
  dobOperator: "",
  dobFrom: "",
  dobTo: "",
  caregiver: "",
  systemCaregiver: "",
  vitals: [],
  serialNumber: "",
  rpmStartFrom: "",
  rpmStartTo: ""
});

const PANEL_FILTER_KEYS = [
  "lastName",
  "firstName",
  "phone",
  "dobOperator",
  "caregiver",
  "systemCaregiver",
  "vitals",
  "serialNumber"
];

const normalizeProgramKey = (value) => String(value || "").trim().toLowerCase();

const extractEnrolledPrograms = (summaryPayload) => {
  const data = summaryPayload?.data?.data ?? summaryPayload?.data ?? summaryPayload;
  const enrolled = data?.enrolled_programs;
  if (!Array.isArray(enrolled)) return [];
  return enrolled
    .map((p) => normalizeProgramKey(typeof p === "string" ? p : (p.short_name || p.id || p.label || "")))
    .filter(Boolean);
};

const extractPatientsPayload = (response) => {
  let patientsData = [];
  let pagination = {};
  if (response && response.data) {
    if (response.data.success) {
      if (response.data.data) {
        if (Array.isArray(response.data.data)) {
          patientsData = response.data.data;
        } else if (response.data.data.patients && Array.isArray(response.data.data.patients)) {
          patientsData = response.data.data.patients;
          pagination = response.data.data.pagination || response.data.data.meta || {};
        } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
          patientsData = response.data.data.data;
          pagination = response.data.data.pagination || response.data.data.meta || {};
        }
      }
    } else if (Array.isArray(response.data)) {
      patientsData = response.data;
    } else if (response.data.patients && Array.isArray(response.data.patients)) {
      patientsData = response.data.patients;
      pagination = response.data.pagination || {};
    } else if (response.data.data && Array.isArray(response.data.data)) {
      patientsData = response.data.data;
      pagination = response.data.pagination || {};
    }
  }
  return {
    patients: Array.isArray(patientsData) ? patientsData : [],
    total: pagination.total || 0
  };
};

const LookupPatient = () => {
  const { user } = useSelector((state) => state.auth);
  const roleId = Number(user?.role_id);
  const isSystemCaregiver = isSystemCaregiverRole(roleId);
  const isScopedRole = [3, 4, 5, 7].includes(roleId); // Practice Admin, Provider, Caregiver, System Caregiver
  const showProviderFilter = roleId !== 4 && roleId !== 5;
  const showCaregiverFilter = roleId !== 5;
  const scopedPracticeId = useMemo(() => {
    const pid = user?.practice_id ?? user?.practiceId ?? user?.practices?.[0]?.id;
    return pid != null && pid !== "" ? String(pid) : "";
  }, [user?.practiceId, user?.practice_id, user?.practices]);
  const navigate = useNavigate();
  const { updateBreadcrumbs } = useBreadcrumb();

  const lookupBaseCrumb = useMemo(() => {
    const practiceHome = scopedPracticeId ? `/practices/${scopedPracticeId}` : "/dashboard";
    return roleId === 3
      ? { label: "Practice Dashboard", path: practiceHome }
      : roleId === 4
        ? { label: "Patient Management", path: null }
        : roleId === 5
          ? { label: "Caregiver Dashboard", path: "/caregiver-dashboard" }
          : roleId === 7
            ? { label: "System Caregiver Dashboard", path: "/system-caregiver-dashboard" }
            : { label: "Patient Management", path: null };
  }, [roleId, scopedPracticeId]);

  useEffect(() => {
    updateBreadcrumbs([
      lookupBaseCrumb,
      { label: "Look up Patient", path: null, active: true }
    ]);
  }, [lookupBaseCrumb, updateBreadcrumbs]);

  const [formData, setFormData] = useState(() => emptyLookupFilters());

  const [patients, setPatients] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState("10");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [activePracticeId, setActivePracticeId] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    lastName: "",
    firstName: "",
    practice: "",
    provider: "",
    caregiver: "",
    systemCaregiver: "",
    status: "",
    vitals: "",
    serialNumber: "",
    phone: "",
    dobOperator: "",
    dobFrom: "",
    dobTo: "",
    rpmStartFrom: "",
    rpmStartTo: "",
    programEnrolled: PROGRAM_SELECT
  });
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const hasInitializedRef = useRef(false);
  const latestPatientsRequestRef = useRef(0);
  const [hasQueryAttempted, setHasQueryAttempted] = useState(false);
  const [resultTab, setResultTab] = useState(PROGRAM_RPM); // rpm | ccm when both
  const [isExporting, setIsExporting] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // "export" | "more" | "filters" | null
  const titleActionsRef = useRef(null);
  const filtersAnchorRef = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const [hasRpm, setHasRpm] = useState(true);
  const [hasCcm, setHasCcm] = useState(true);
  const [hasPcm, setHasPcm] = useState(true);
  const [programsLoading, setProgramsLoading] = useState(false);
  const availablePrograms = useMemo(
    () => [
      ...(hasRpm ? [PROGRAM_RPM] : []),
      ...(hasCcm ? [PROGRAM_CCM] : []),
      ...(hasPcm ? [PROGRAM_PCM] : [])
    ],
    [hasRpm, hasCcm, hasPcm]
  );
  const hasMultiplePrograms = availablePrograms.length > 1;
  const activeResultProgram = useMemo(() => {
    const applied = appliedFilters.programEnrolled;
    if (applied && availablePrograms.includes(applied)) return applied;
    if (availablePrograms.includes(resultTab)) return resultTab;
    return availablePrograms[0] || PROGRAM_RPM;
  }, [availablePrograms, resultTab, appliedFilters.programEnrolled]);

  const [practices, setPractices] = useState([]);
  const [assignedPractices, setAssignedPractices] = useState([]);
  const systemCaregiverMultiPractice =
    isSystemCaregiver && assignedPractices.length > 1;
  const showPracticeField = !isScopedRole || systemCaregiverMultiPractice;
  const lockedPracticeId = useMemo(() => {
    if (!isScopedRole) return "";
    if (isSystemCaregiver && assignedPractices.length === 1) {
      return String(assignedPractices[0].id);
    }
    return scopedPracticeId;
  }, [isScopedRole, isSystemCaregiver, assignedPractices, scopedPracticeId]);
  const allPracticeOptions = isSystemCaregiver ? assignedPractices : practices;
  const practicesForProgram = useCallback((program, options = allPracticeOptions) => {
    const key = String(program || "").toLowerCase();
    if (key !== PROGRAM_CCM && key !== PROGRAM_PCM) return options;
    return options.filter((practice) => {
      const flags = getPracticeProgramFlags(practice);
      return key === PROGRAM_CCM ? flags.hasCcm : flags.hasPcm;
    });
  }, [allPracticeOptions]);

  const effectivePracticeId = systemCaregiverMultiPractice
    ? (formData.practice || "all")
    : isScopedRole
      ? lockedPracticeId
      : (formData.practice || "all");

  const isPracticeSelected = useMemo(() => {
    const p = String(appliedFilters.practice || activePracticeId || formData.practice || "").trim().toLowerCase();
    return Boolean(p && p !== "all" && p !== "0");
  }, [appliedFilters.practice, activePracticeId, formData.practice]);

  const showPracticeColumn = !isPracticeSelected;

  const showRpmFilters = activeResultProgram === PROGRAM_RPM;
  const practiceDropdownList = useMemo(
    () => practicesForProgram(activeResultProgram),
    [activeResultProgram, practicesForProgram]
  );

  const todayIso = useMemo(() => getTodayDateOnlyLocal(), []);
  const showDobTo = formData.dobOperator === "between";

  const apiLimit = useMemo(() => {
    if (entriesPerPage === "all" || entriesPerPage === "All") return 10000;
    const n = parseInt(entriesPerPage, 10);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, [entriesPerPage]);

  const [providers, setProviders] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [systemCaregivers, setSystemCaregivers] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [isVitalsDropdownOpen, setIsVitalsDropdownOpen] = useState(false);
  const vitalsDropdownRef = useRef(null);

  const loadSystemCaregivers = useCallback(async (practiceId) => {
    try {
      const params = { limit: 1000, status: "active" };
      if (practiceId && practiceId !== "all" && practiceId !== "0") {
        params.practiceId = practiceId;
      }
      const response = await systemCaregiverService.getSystemCaregivers(params);
      let list = [];
      if (response?.data?.success) {
        list = response.data.data?.caregivers || response.data.data?.systemCaregivers || response.data.data || [];
      } else if (Array.isArray(response?.data)) {
        list = response.data;
      }
      const activeOnly = (Array.isArray(list) ? list : []).filter((sc) => {
        const s = String(sc?.status ?? sc?.user_status ?? sc?.status_id ?? "").toLowerCase();
        return s === "active" || s === "2" || s === "a" || s === "enabled" || !s;
      });
      setSystemCaregivers(activeOnly);

      setFormData((prev) => {
        if (!prev.systemCaregiver) return prev;
        const stillExists = activeOnly.some((sc) => String(sc.id) === String(prev.systemCaregiver));
        if (!stillExists) {
          return { ...prev, systemCaregiver: "" };
        }
        return prev;
      });
    } catch (error) {
      console.error("Error loading system caregivers:", error);
      setSystemCaregivers([]);
    }
  }, []);

  const statusOptions = [
    { value: "2", label: "Active" },
    { value: "3", label: "Pending" },
    { value: "4", label: "Locked" }
  ];

  const vitalsOptions = [
    { value: "1", label: "BP" },
    { value: "2", label: "BG" },
    { value: "3", label: "WT" }
  ];

  const loadPracticePrograms = useCallback(async (practiceId) => {
    if (!practiceId) {
      setHasRpm(false);
      setHasCcm(false);
      setHasPcm(false);
      return;
    }
    setProgramsLoading(true);
    try {
      const response = await practiceService.getPracticeSummary(practiceId);
      const programs = extractEnrolledPrograms(response);
      const rpm = programs.some((p) => p === PROGRAM_RPM);
      const ccm = programs.some((p) => p === PROGRAM_CCM);
      const pcm = programs.some((p) => p === PROGRAM_PCM);
      setHasRpm(rpm);
      setHasCcm(ccm);
      setHasPcm(pcm);
      const enrolled = [rpm && PROGRAM_RPM, ccm && PROGRAM_CCM, pcm && PROGRAM_PCM].filter(Boolean);
      setFormData((prev) => ({
        ...prev,
        programEnrolled: enrolled.length === 1 ? enrolled[0] : PROGRAM_SELECT
      }));
      setResultTab(enrolled[0] || PROGRAM_RPM);
    } catch (error) {
      console.error("Error loading practice programs:", error);
      setHasRpm(false);
      setHasCcm(false);
      setHasPcm(false);
      toast.error("Failed to load practice programs");
    } finally {
      setProgramsLoading(false);
    }
  }, []);

  const loadPractices = async () => {
    try {
      setLoadingDropdowns(true);
      const response = await practiceService.getPractices({ limit: 1000, page: 1 });

      let practicesData = [];
      if (response && response.data) {
        if (response.data.success) {
          if (response.data.data) {
            if (Array.isArray(response.data.data)) {
              practicesData = response.data.data;
            } else if (response.data.data.practices && Array.isArray(response.data.data.practices)) {
              practicesData = response.data.data.practices;
            } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
              practicesData = response.data.data.data;
            }
          }
        } else if (Array.isArray(response.data)) {
          practicesData = response.data;
        } else if (response.data.practices && Array.isArray(response.data.practices)) {
          practicesData = response.data.practices;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          practicesData = response.data.data;
        }
      }

      const uniquePractices = practicesData.filter((practice, index, self) =>
        index === self.findIndex((p) => p.id === practice.id)
      );
      setPractices(Array.isArray(uniquePractices) ? uniquePractices : []);
    } catch (error) {
      console.error("Error loading practices:", error);
      toast.error("Failed to load practices");
      setPractices([]);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const loadProvidersAndCaregivers = async (practiceId) => {
    try {
      setLoadingDropdowns(true);
      const [providersRes, caregiversRes] = await Promise.all([
        practiceService.getPracticeProviders(practiceId),
        practiceService.getPracticeCaregivers(practiceId)
      ]);

      let providersData = [];
      if (providersRes && providersRes.data) {
        if (providersRes.data.success) {
          if (providersRes.data.data) {
            if (Array.isArray(providersRes.data.data)) {
              providersData = providersRes.data.data;
            } else if (providersRes.data.data.providers && Array.isArray(providersRes.data.data.providers)) {
              providersData = providersRes.data.data.providers;
            }
          }
        } else if (Array.isArray(providersRes.data)) {
          providersData = providersRes.data;
        } else if (providersRes.data.providers && Array.isArray(providersRes.data.providers)) {
          providersData = providersRes.data.providers;
        } else if (providersRes.data.data && Array.isArray(providersRes.data.data)) {
          providersData = providersRes.data.data;
        }
      }

      const uniqueProviders = providersData.filter((provider, index, self) =>
        index === self.findIndex((p) => p.id === provider.id)
      );
      setProviders(Array.isArray(uniqueProviders) ? uniqueProviders : []);

      let caregiversData = [];
      if (caregiversRes && caregiversRes.data) {
        if (caregiversRes.data.success) {
          if (caregiversRes.data.data) {
            if (Array.isArray(caregiversRes.data.data)) {
              caregiversData = caregiversRes.data.data;
            } else if (caregiversRes.data.data.caregivers && Array.isArray(caregiversRes.data.data.caregivers)) {
              caregiversData = caregiversRes.data.data.caregivers;
            }
          }
        } else if (Array.isArray(caregiversRes.data)) {
          caregiversData = caregiversRes.data;
        } else if (caregiversRes.data.caregivers && Array.isArray(caregiversRes.data.caregivers)) {
          caregiversData = caregiversRes.data.caregivers;
        } else if (caregiversRes.data.data && Array.isArray(caregiversRes.data.data)) {
          caregiversData = caregiversRes.data.data;
        }
      }

      const uniqueCaregivers = caregiversData.filter((caregiver, index, self) =>
        index === self.findIndex((c) => c.id === caregiver.id)
      );
      setCaregivers(Array.isArray(uniqueCaregivers) ? uniqueCaregivers : []);
    } catch (error) {
      console.error("Error loading providers/caregivers:", error);
      toast.error("Failed to load providers/caregivers");
      setProviders([]);
      setCaregivers([]);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // Initial bootstrap: practices for unscoped, lock practice + programs for scoped
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const initializeData = async () => {
      if (isSystemCaregiver && user?.id) {
        try {
          const assigned = await fetchAssignedPracticesForSystemCaregiver(
            practiceService,
            user.id
          );
          setAssignedPractices(assigned);
          if (assigned.length === 1) {
            const pid = String(assigned[0].id);
            setFormData((prev) => ({ ...prev, practice: pid }));
            await Promise.all([
              loadProvidersAndCaregivers(pid),
              loadPracticePrograms(pid)
            ]);
          }
        } catch (error) {
          console.error("Error loading assigned practices:", error);
          toast.error("Failed to load your assigned practices");
          setAssignedPractices([]);
        }
        return;
      }
      if (isScopedRole) {
        if (scopedPracticeId) {
          setFormData((prev) => ({ ...prev, practice: scopedPracticeId }));
          await Promise.all([
            loadProvidersAndCaregivers(scopedPracticeId),
            loadPracticePrograms(scopedPracticeId)
          ]);
        }
        return;
      }
      await Promise.all([loadPractices(), loadSystemCaregivers(formData.practice)]);
    };

    initializeData();
  }, [isScopedRole, isSystemCaregiver, scopedPracticeId, loadPracticePrograms, loadSystemCaregivers, user?.id]);

  // Providers/caregivers/systemCaregivers when practice changes (unscoped)
  useEffect(() => {
    if (isScopedRole && !systemCaregiverMultiPractice) return;
    const targetPracticeId = formData.practice;
    if (targetPracticeId) {
      loadProvidersAndCaregivers(targetPracticeId);
      loadPracticePrograms(targetPracticeId);
      loadSystemCaregivers(targetPracticeId);
    } else {
      setProviders([]);
      setCaregivers([]);
      setHasRpm(true);
      setHasCcm(true);
      setHasPcm(true);
      loadSystemCaregivers();
    }
  }, [formData.practice, isScopedRole, systemCaregiverMultiPractice, loadPracticePrograms, loadSystemCaregivers]);

  const fetchPatientsForProgram = useCallback(async (
    practiceId,
    program,
    page = 1,
    limit = 10,
    options = {},
    filterOverride = null
  ) => {
    const { silent = false, showToast = false } = options;
    const targetId = practiceId || "all";
    if (!program) return;
    const requestId = ++latestPatientsRequestRef.current;

    if (!silent) {
      setTableLoading(true);
      setPatients([]);
    }
    try {
      const sourceFilters = filterOverride || appliedFilters;
      const params = {
        program: String(program).toLowerCase(),
        page,
        limit,
        status: sourceFilters.status || "",
        lastName: sourceFilters.lastName || "",
        firstName: sourceFilters.firstName || "",
        providerId: sourceFilters.provider || "",
        caregiverId: sourceFilters.caregiver || "",
        systemCaregiverId: sourceFilters.systemCaregiver || "",
        phone: sourceFilters.phone || "",
        dobOperator: sourceFilters.dobOperator || "",
        dobFrom: sourceFilters.dobFrom || "",
        dobTo: sourceFilters.dobTo || "",
        billingStartRpm: sourceFilters.rpmStartFrom || "",
        billingEndRpm: sourceFilters.rpmStartTo || "",
        nameOrSerial: debouncedSearch
      };
      if (String(program).toLowerCase() === PROGRAM_RPM) {
        params.vitals = sourceFilters.vitals || "";
        params.serialNumber = sourceFilters.serialNumber || "";
      }

      practiceService.invalidatePracticePatientsCache(targetId);
      const response = await practiceService.getPracticePatientsFilteredByProgramTab(targetId, params);
      const { patients: patientsData, total } = extractPatientsPayload(response);

      if (requestId !== latestPatientsRequestRef.current) return;
      setPatients(patientsData);
      setTotalPatients(total || patientsData.length);
    } catch (error) {
      console.error("Error fetching patients:", error);
      if (requestId !== latestPatientsRequestRef.current) return;
      if (showToast) {
        toast.error("Request failed", { toastId: "lookup-patient-query-error" });
      }
      setPatients([]);
      setTotalPatients(0);
    } finally {
      if (!silent) setTableLoading(false);
    }
  }, [appliedFilters, debouncedSearch]);

  useEffect(() => {
    const next = searchTerm.trim();
    const timer = setTimeout(() => setDebouncedSearch(next), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch after Query / tab / pagination changes (handlers only update state)
  useEffect(() => {
    if (!hasQueryAttempted) return;
    const targetId = activePracticeId || effectivePracticeId || "all";

    fetchPatientsForProgram(
      targetId,
      activeResultProgram,
      currentPage,
      apiLimit,
      { silent: false, showToast: true },
      appliedFilters
    );
  }, [apiLimit, currentPage, activePracticeId, effectivePracticeId, activeResultProgram, hasQueryAttempted, appliedFilters, fetchPatientsForProgram]);

  // Load all patients by default on initial mount (no practice selection required)
  useEffect(() => {
    if (programsLoading) return;
    if (hasQueryAttempted) return;

    setHasQueryAttempted(true);
    setActivePracticeId(effectivePracticeId || "all");
    setCurrentPage(1);
  }, [
    programsLoading,
    hasQueryAttempted,
    effectivePracticeId
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === "serialNumber") {
      nextValue = normalizeDeviceSerialInput(value);
    }
    if (name === "practice") {
      setFormData((prev) => ({
        ...prev,
        practice: nextValue,
        provider: "",
        caregiver: "",
        programEnrolled: PROGRAM_SELECT,
        vitals: [],
        serialNumber: "",
        phone: "",
        dobOperator: "",
        dobFrom: "",
        dobTo: "",
        rpmStartFrom: "",
        rpmStartTo: ""
      }));
      if (nextValue) {
        loadProvidersAndCaregivers(nextValue);
        loadPracticePrograms(nextValue);
      }
      setAppliedFilters((prev) => ({
        ...prev,
        practice: nextValue,
        provider: "",
        caregiver: "",
        vitals: "",
        serialNumber: "",
        phone: "",
        dobOperator: "",
        dobFrom: "",
        dobTo: "",
        rpmStartFrom: "",
        rpmStartTo: "",
        programEnrolled: PROGRAM_SELECT
      }));
      setHasQueryAttempted(false);
      setPatients([]);
      setTotalPatients(0);
      setActivePracticeId("");
      return;
    }
    if (name === "programEnrolled") {
      setFormData((prev) => ({ ...prev, programEnrolled: nextValue }));
      if (availablePrograms.includes(nextValue)) setResultTab(nextValue);
      return;
    }
    if (name === "dobOperator") {
      setFormData((prev) => ({
        ...prev,
        dobOperator: nextValue,
        ...(nextValue !== "between" ? { dobTo: "" } : {})
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handlePhoneChange = (e) => {
    setFormData((prev) => ({ ...prev, phone: e.target.value || "" }));
  };

  const handleDobFromChange = (iso) => {
    setFormData((prev) => ({ ...prev, dobFrom: iso || "" }));
  };

  const handleDobToChange = (iso) => {
    setFormData((prev) => ({ ...prev, dobTo: iso || "" }));
  };

  const toggleVitalOption = (value) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.vitals) ? prev.vitals : [];
      const exists = current.includes(value);
      return {
        ...prev,
        vitals: exists ? current.filter((v) => v !== value) : [...current, value]
      };
    });
  };

  useEffect(() => {
    const onClickOutside = (event) => {
      if (vitalsDropdownRef.current && !vitalsDropdownRef.current.contains(event.target)) {
        setIsVitalsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  /** Validate a filter set and make it the active query. Returns false when validation fails. */
  const applyFilters = (f) => {
    const practiceId = effectivePracticeId;
    if (!practiceId) {
      toast.error("Practice is required");
      return false;
    }

    if (f.dobOperator) {
      if (!f.dobFrom) {
        toast.error("Date of birth is required for the selected DOB filter");
        return false;
      }
      if (f.dobFrom > todayIso) {
        toast.error("Date of birth cannot be in the future");
        return false;
      }
      if (f.dobOperator === "between") {
        if (!f.dobTo) {
          toast.error("DOB (to) is required when using Between");
          return false;
        }
        if (f.dobTo > todayIso) {
          toast.error("DOB (to) cannot be in the future");
          return false;
        }
        if (f.dobTo < f.dobFrom) {
          toast.error("DOB (to) must be on or after DOB (from)");
          return false;
        }
      }
    } else if (f.dobFrom || f.dobTo) {
      toast.error("Select a DOB operator (On, On or before, On or after, or Between)");
      return false;
    }

    if (f.rpmStartFrom || f.rpmStartTo) {
      if (f.rpmStartFrom && f.rpmStartFrom > todayIso) {
        toast.error("RPM Service Date (from) cannot be in the future");
        return false;
      }
      if (f.rpmStartTo && f.rpmStartTo > todayIso) {
        toast.error("RPM Service Date (to) cannot be in the future");
        return false;
      }
      if (f.rpmStartFrom && f.rpmStartTo && f.rpmStartTo < f.rpmStartFrom) {
        toast.error("RPM Service Date (to) must be on or after RPM Service Date (from)");
        return false;
      }
    }

    const programEnrolled = availablePrograms.includes(f.programEnrolled)
      ? f.programEnrolled
      : activeResultProgram;

    const nextAppliedFilters = {
      lastName: f.lastName || "",
      firstName: f.firstName || "",
      practice: f.practice || "",
      provider: f.provider || "",
      caregiver: f.caregiver || "",
      systemCaregiver: f.systemCaregiver || "",
      status: f.status || "",
      vitals: Array.isArray(f.vitals) ? f.vitals.join(",") : (f.vitals || ""),
      serialNumber: f.serialNumber || "",
      phone: normalizePhoneDigits(f.phone) || "",
      dobOperator: f.dobOperator || "",
      dobFrom: f.dobFrom || "",
      dobTo: f.dobOperator === "between" ? (f.dobTo || "") : "",
      rpmStartFrom: f.rpmStartFrom || "",
      rpmStartTo: f.rpmStartTo || "",
      programEnrolled
    };

    if (programEnrolled) setResultTab(programEnrolled);
    setHasQueryAttempted(true);
    setActivePracticeId(String(practiceId));
    setAppliedFilters(nextAppliedFilters);
    setCurrentPage(1);
    return true;
  };

  /** Toolbar controls (status, provider, program) apply immediately. */
  const setAndApply = (patch) => {
    const next = { ...formData, ...patch };
    setFormData(next);
    applyFilters(next);
  };

  const handleApplyMoreFilters = () => {
    if (applyFilters(formData)) setOpenMenu(null);
  };

  const handleClearMoreFilters = () => {
    const next = { ...formData, ...emptyPanelFilters() };
    setFormData(next);
    setIsVitalsDropdownOpen(false);
    applyFilters(next);
  };

  const handleRemoveFilterTag = (keys) => {
    const patch = {};
    keys.forEach((k) => {
      patch[k] = k === "vitals" ? [] : "";
    });
    setAndApply(patch);
  };

  const handleProgramChipChange = (program) => {
    if (program === activeResultProgram) return;
    const selectedPractice = String(formData.practice || "");
    const practiceStillValid = !selectedPractice || practicesForProgram(program).some(
      (practice) => String(practice.id) === selectedPractice
    );
    setAndApply({
      programEnrolled: program,
      ...(practiceStillValid ? {} : { practice: "" })
    });
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortPatients = (patientsList) => {
    const getStatusOrder = (patient) => getPatientStatusSortOrder(resolvePatientStatusRaw(patient));

    const getSortValue = (patient, key) => {
      switch (key) {
        case "status":
          return getStatusOrder(patient);
        case "name":
          return (
            patient.name ||
            patient.patient_name ||
            `${patient.last_name || ""}, ${patient.first_name || ""}`
          ).toLowerCase();
        case "vitals":
          return String(patient.vitals || "").toLowerCase();
        case "systolic":
          return Number(patient.last_systolic ?? patient.systolic ?? 0);
        case "diastolic":
          return Number(patient.last_diastolic ?? patient.diastolic ?? 0);
        case "pulse":
          return Number(patient.last_pulse ?? patient.pulse ?? 0);
        case "glucose":
          return Number(patient.last_glucose ?? patient.glucose ?? 0);
        case "weight":
          return Number(patient.last_weight ?? patient.weight ?? 0);
        case "last_upload":
          return patient.last_upload_date ? String(patient.last_upload_date) : "";
        case "readings":
          return Number(patient.readings_count || patient.number_of_readings || 0);
        case "service_time":
          return Number(patient.service_time || 0);
        case "dob":
          return patient.date_of_birth ? String(patient.date_of_birth) : "";
        case "chronic_conditions":
          return formatChronicDiseaseNames(patient.chronic_conditions || patient.icd_codes).toLowerCase();
        case "last_activity":
          return patient.last_activity_date ? String(patient.last_activity_date) : "";
        case "billing":
          return String(patient.billing_status || "").toLowerCase();
        default:
          return patient[key] ?? "";
      }
    };

    return [...patientsList].sort((a, b) => {
      const skipStatusGroup = sortConfig.key === "service_time";
      // Default grouping: Active → Pending → Locked unless sorting by status or service time.
      if (!skipStatusGroup && sortConfig.key !== "status") {
        const statusA = getStatusOrder(a);
        const statusB = getStatusOrder(b);
        if (statusA !== statusB) {
          return statusA - statusB;
        }
      }

      let aValue = getSortValue(a, sortConfig.key);
      let bValue = getSortValue(b, sortConfig.key);

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Status DESC = Locked → Pending → Active (L, P, A)
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const sortedAndFilteredPatients = sortPatients(patients);
  const totalPages = Math.max(1, Math.ceil(totalPatients / apiLimit) || 1);
  const hasGridRows = hasQueryAttempted && sortedAndFilteredPatients.length > 0;

  const getPatientDisplayName = (patient) =>
    patient?.name ||
    patient?.patient_name ||
    `${patient?.last_name || ""}, ${patient?.first_name || ""}`.replace(/^,\s*|\s*,$/g, "").trim() ||
    "N/A";

  const currentUserLabel = useMemo(() => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
    return fullName || user?.username || user?.email || "Unknown user";
  }, [user]);

  const practiceNameForCriteria = useMemo(() => {
    const pid = String(activePracticeId || effectivePracticeId || "");
    if (!pid) return "";
    const fromList =
      practiceDropdownList.find((p) => String(p.id) === pid) ||
      practices.find((p) => String(p.id) === pid) ||
      assignedPractices.find((p) => String(p.id) === pid);
    return fromList?.practice_name || fromList?.name || pid;
  }, [activePracticeId, effectivePracticeId, practiceDropdownList, practices, assignedPractices]);

  const criteriaLines = useMemo(() => {
    const lines = [];
    if (practiceNameForCriteria) lines.push(`Practice: ${practiceNameForCriteria}`);
    if (appliedFilters.lastName) lines.push(`Last Name: ${appliedFilters.lastName}`);
    if (appliedFilters.firstName) lines.push(`First Name: ${appliedFilters.firstName}`);
    if (appliedFilters.phone) {
      lines.push(`Phone: ${formatPhoneForDisplay(appliedFilters.phone) || appliedFilters.phone}`);
    }
    if (appliedFilters.dobOperator && appliedFilters.dobFrom) {
      const opLabel =
        DOB_OPERATORS.find((o) => o.value === appliedFilters.dobOperator)?.label ||
        appliedFilters.dobOperator;
      if (appliedFilters.dobOperator === "between" && appliedFilters.dobTo) {
        lines.push(`DOB: ${opLabel} ${formatDateOnlyUs(appliedFilters.dobFrom) || appliedFilters.dobFrom} – ${formatDateOnlyUs(appliedFilters.dobTo) || appliedFilters.dobTo}`);
      } else {
        lines.push(`DOB: ${opLabel} ${formatDateOnlyUs(appliedFilters.dobFrom) || appliedFilters.dobFrom}`);
      }
    }
    if (appliedFilters.programEnrolled) {
      const programLabel =
        appliedFilters.programEnrolled === PROGRAM_BOTH
          ? "Both"
          : String(appliedFilters.programEnrolled).toUpperCase();
      lines.push(`Program Enrolled: ${programLabel}`);
    }
    if (appliedFilters.provider) {
      const provider = providers.find((p) => String(p.id) === String(appliedFilters.provider));
      const providerName = provider
        ? `${provider.last_name || ""}, ${provider.first_name || ""}`.replace(/^,\s*|\s*,$/g, "").trim()
        : appliedFilters.provider;
      lines.push(`Provider: ${providerName}`);
    }
    if (appliedFilters.caregiver) {
      const caregiver = caregivers.find((c) => String(c.id) === String(appliedFilters.caregiver));
      const caregiverName = caregiver
        ? `${caregiver.last_name || ""}, ${caregiver.first_name || ""}`.replace(/^,\s*|\s*,$/g, "").trim()
        : appliedFilters.caregiver;
      lines.push(`Caregiver: ${caregiverName}`);
    }
    if (appliedFilters.systemCaregiver) {
      const sc = systemCaregivers.find((s) => String(s.id) === String(appliedFilters.systemCaregiver));
      const scName = sc
        ? `${sc.last_name || ""}, ${sc.first_name || ""}`.replace(/^,\s*|\s*,$/g, "").trim() || sc.name || sc.username
        : appliedFilters.systemCaregiver;
      lines.push(`System Caregiver: ${scName}`);
    }
    if (appliedFilters.status) {
      const statusLabel =
        statusOptions.find((s) => String(s.value) === String(appliedFilters.status))?.label ||
        appliedFilters.status;
      lines.push(`Status: ${statusLabel}`);
    }
    if (appliedFilters.vitals) {
      const vitalIds = String(appliedFilters.vitals)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      const vitalLabels = vitalIds
        .map((id) => vitalsOptions.find((v) => String(v.value) === String(id))?.label || id)
        .join(", ");
      if (vitalLabels) lines.push(`Vitals: ${vitalLabels}`);
    }
    if (appliedFilters.serialNumber) lines.push(`Serial #: ${appliedFilters.serialNumber}`);
    if (searchTerm.trim()) lines.push(`Search: ${searchTerm.trim()}`);
    if (lines.length === 0) lines.push("Criteria: All patients (no filters)");
    return lines;
  }, [
    practiceNameForCriteria,
    appliedFilters,
    activeResultProgram,
    providers,
    caregivers,
    statusOptions,
    vitalsOptions,
    searchTerm
  ]);

  const printTimestamp = new Date().toLocaleString();

  const defaultEmailSubject = useMemo(() => {
    const practicePart = practiceNameForCriteria ? ` — ${practiceNameForCriteria}` : "";
    return `Look up Patient${practicePart}`;
  }, [practiceNameForCriteria]);

  const defaultEmailContent = useMemo(() => {
    return [
      "Please find the Look up Patient results attached as a CSV file.",
      "",
      "Query criteria:",
      ...criteriaLines.map((line) => `• ${line}`),
      "",
      `Generated: ${new Date().toLocaleString()}`,
      `Generated by: ${currentUserLabel}`
    ].join("\n");
  }, [criteriaLines, currentUserLabel]);

  const fetchAllMatchingPatients = useCallback(async () => {
    const practiceId = activePracticeId || effectivePracticeId || appliedFilters.practice || "all";
    const program = activeResultProgram;
    if (!program) return [];

    const sourceFilters = appliedFilters;
    const params = {
      program: String(program).toLowerCase(),
      page: 1,
      limit: 10000,
      status: sourceFilters.status || "",
      lastName: sourceFilters.lastName || "",
      firstName: sourceFilters.firstName || "",
      providerId: sourceFilters.provider || "",
      caregiverId: sourceFilters.caregiver || "",
      systemCaregiverId: sourceFilters.systemCaregiver || "",
      phone: sourceFilters.phone || "",
      dobOperator: sourceFilters.dobOperator || "",
      dobFrom: sourceFilters.dobFrom || "",
      dobTo: sourceFilters.dobTo || "",
      billingStartRpm: sourceFilters.rpmStartFrom || "",
      billingEndRpm: sourceFilters.rpmStartTo || "",
      nameOrSerial: searchTerm.trim()
    };
    if (String(program).toLowerCase() === PROGRAM_RPM) {
      params.vitals = sourceFilters.vitals || "";
      params.serialNumber = sourceFilters.serialNumber || "";
    }

    practiceService.invalidatePracticePatientsCache(practiceId);
    const response = await practiceService.getPracticePatientsFilteredByProgramTab(practiceId, params);
    const { patients: patientsData } = extractPatientsPayload(response);
    const rows = Array.isArray(patientsData) ? patientsData : [];

    return sortPatients(rows);
  }, [activePracticeId, activeResultProgram, appliedFilters, searchTerm, sortConfig]);

  const availableEmailColumns = useMemo(() => {
    const isCcm = activeResultProgram !== PROGRAM_RPM;
    return isCcm
      ? [
          "Status",
          "Patient Name",
          ...(showPracticeColumn ? ["Practice Name"] : []),
          "DOB",
          "Chronic Conditions",
          "Last Activity",
          "Service Time",
          "Billing"
        ]
      : [
          "Status",
          "Vitals",
          "Name",
          ...(showPracticeColumn ? ["Practice Name"] : []),
          "Systolic",
          "Diastolic",
          "Pulse",
          "Glucose",
          "Weight",
          "Last Upload",
          "# Readings",
          "Time"
        ];
  }, [activeResultProgram, showPracticeColumn]);

  const getLookupExportTableData = (rows, selectedHeaderSet = null) => {
    const isCcm = activeResultProgram !== PROGRAM_RPM;
    let headers = isCcm
      ? [
          "#",
          "Status",
          "Patient Name",
          ...(showPracticeColumn ? ["Practice Name"] : []),
          "DOB",
          "Chronic Conditions",
          "Last Activity",
          "Service Time",
          "Billing"
        ]
      : [
          "#",
          "Status",
          "Vitals",
          "Name",
          ...(showPracticeColumn ? ["Practice Name"] : []),
          "Systolic",
          "Diastolic",
          "Pulse",
          "Glucose",
          "Weight",
          "Last Upload",
          "# Readings",
          "Time"
        ];

    let dataRows = rows.map((p, idx) => {
      if (isCcm) {
        return [
          plainExportCell(idx + 1),
          plainExportCell(patientStatusLabel(p?.status ?? p?.status_id ?? p?.patient_status)),
          plainExportCell(getPatientDisplayName(p)),
          ...(showPracticeColumn ? [plainExportCell(p.practice_name || p.practice || "N/A")] : []),
          plainExportCell(formatDateOnlyUs(p.date_of_birth) || ""),
          plainExportCell(formatChronicDiseaseNames(p.chronic_conditions || p.icd_codes) || ""),
          plainExportCell(p.last_activity_date ? formatDateOnlyUs(p.last_activity_date) || "" : ""),
          plainExportCell(p.service_time ?? 0),
          plainExportCell(p.billing_status || "Pending")
        ];
      }

      const statuses = getPatientListVitalStatuses(p);
      return [
        plainExportCell(idx + 1),
        plainExportCell(patientStatusLabel(p?.status ?? p?.status_id ?? p?.patient_status)),
        plainExportCell(p.vitals || ""),
        plainExportCell(getPatientDisplayName(p)),
        ...(showPracticeColumn ? [plainExportCell(p.practice_name || p.practice || "N/A")] : []),
        makeVitalExportCell(p.last_systolic ?? p.systolic, statuses.sysStatus),
        makeVitalExportCell(p.last_diastolic ?? p.diastolic, statuses.diaStatus),
        makeVitalExportCell(p.last_pulse ?? p.pulse, statuses.pulseStatus),
        makeVitalExportCell(p.last_glucose ?? p.glucose, statuses.glucoseStatus),
        makeWeightExportCell(p, statuses.weightStatus),
        plainExportCell(formatDateOnlyUs(p.last_upload_date) || ""),
        plainExportCell(p.readings_count ?? p.number_of_readings ?? 0),
        plainExportCell(p.service_time ?? 0)
      ];
    });

    if (selectedHeaderSet && selectedHeaderSet.size > 0) {
      const keepIndices = [];
      headers.forEach((h, i) => {
        if (h === "#" || selectedHeaderSet.has(h)) {
          keepIndices.push(i);
        }
      });
      headers = keepIndices.map((i) => headers[i]);
      dataRows = dataRows.map((row) => keepIndices.map((i) => row[i]));
    }

    return { headers, rows: dataRows };
  };

  const buildLookupCsv = (rows, selectedHeaderSet = null) => {
    const { headers, rows: dataRows } = getLookupExportTableData(rows, selectedHeaderSet);
    const escapeCsv = (cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`;
    const body = [
      headers,
      ...dataRows.map((row) =>
        row.map((cell) => (typeof cell === "object" && cell != null ? cell.csv : cell))
      )
    ]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\r\n");
    return `\uFEFF${body}`;
  };

  const escapeLookupHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const buildLookupExportTableHtml = ({ headers, rows, includeMeta = true }) => {
    const metaBlock = includeMeta
      ? `
      <div style="margin-bottom: 12px; border-bottom: 2px solid ${LOOKUP_TABLE_HEADER_BG}; padding-bottom: 10px;">
        <h2 style="margin: 0 0 6px 0; font-size: 16px; color: ${LOOKUP_TABLE_HEADER_BG};">Look up Patient</h2>
        <div style="display:flex;justify-content:space-between;font-size:11px;color:#666;margin-bottom:6px;">
          <span>Generated: ${escapeLookupHtml(new Date().toLocaleString())}</span>
          <span>User: ${escapeLookupHtml(currentUserLabel)}</span>
        </div>
        <div style="font-size:11px;color:#666;line-height:1.4;">
          ${criteriaLines.map((line) => `<div>${escapeLookupHtml(line)}</div>`).join("")}
        </div>
        <div style="font-size:11px;color:#666;margin-top:4px;">Total records: ${rows.length}</div>
      </div>`
      : "";

    return `
      ${metaBlock}
      <table style="width:100%;border-collapse:collapse;table-layout:fixed;font-size:9px;">
        <thead>
          <tr>
            ${headers
              .map(
                (h) =>
                  `<th style="background-color:${LOOKUP_TABLE_HEADER_BG};color:#fff;border:1px solid #005f8f;padding:6px 5px;text-align:left;font-weight:700;text-transform:uppercase;">${escapeLookupHtml(h)}</th>`
              )
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${rows
            .map((row, idx) => {
              const bg = idx % 2 === 0 ? "#ffffff" : "#f5f7fa";
              return `<tr style="background-color:${bg};page-break-inside:avoid;break-inside:avoid;">
                ${row
                  .map((cell) => {
                    const text = typeof cell === "object" && cell != null ? cell.text : cell;
                    const color =
                      typeof cell === "object" && cell != null && cell.color ? cell.color : "#111";
                    const weight =
                      typeof cell === "object" && cell != null && cell.color ? "700" : "400";
                    return `<td style="border:1px solid #d0d7de;padding:5px;vertical-align:top;word-break:break-word;color:${color};font-weight:${weight};">${escapeLookupHtml(text)}</td>`;
                  })
                  .join("")}
              </tr>`;
            })
            .join("")}
        </tbody>
      </table>
      <div style="margin-top:10px;font-size:10px;text-align:center;color:#555;">Total Records: ${rows.length}</div>
    `;
  };

  const lookupCsvFilename = () => {
    const dateStr = new Date().toISOString().split("T")[0];
    return `evitals-patient-lookup-${dateStr}.csv`;
  };

  const lookupPdfFilename = () => {
    const dateStr = new Date().toISOString().split("T")[0];
    return `evitals-patient-lookup-${dateStr}.pdf`;
  };

  const handlePrint = () => {
    if (!hasGridRows) {
      toast.warning("No data to print");
      return;
    }
    setShowPrintModal(true);
  };

  const handlePerformPrint = async (selectedColumns) => {
    setShowPrintModal(false);
    let iframe = null;
    try {
      const patients = await fetchAllMatchingPatients();
      if (patients.length === 0) {
        toast.warning("No data to print");
        return;
      }

      const selectedHeaderSet =
        Array.isArray(selectedColumns) && selectedColumns.length > 0
          ? new Set(selectedColumns)
          : null;

      const tableData = getLookupExportTableData(patients, selectedHeaderSet);

      // Hidden iframe avoids browser pop-up blockers (no window.open).
      iframe = document.createElement("iframe");
      iframe.setAttribute("title", "Lookup Patient Print");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.opacity = "0";
      iframe.style.pointerEvents = "none";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow?.document;
      if (!doc) {
        throw new Error("Unable to create print frame");
      }

      doc.open();
      doc.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Look up Patient</title>
  <style>
    @page { size: letter landscape; margin: 0.4in; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 12px;
      font-family: Arial, Helvetica, sans-serif;
      color: #111;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tr { page-break-inside: avoid; break-inside: avoid; }
    table { page-break-inside: auto; }
  </style>
</head>
<body>
  ${buildLookupExportTableHtml(tableData)}
</body>
</html>`);
      doc.close();

      const frameWindow = iframe.contentWindow;
      const cleanup = () => {
        if (iframe && iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };

      const triggerPrint = () => {
        try {
          frameWindow.focus();
          frameWindow.print();
        } catch (err) {
          console.error("Lookup patient print failed:", err);
          toast.error("Failed to print");
        } finally {
          setTimeout(cleanup, 1000);
        }
      };

      setTimeout(triggerPrint, 300);
    } catch (err) {
      console.error("Lookup patient print failed:", err);
      toast.error("Failed to print");
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }
  };

  const handleExportCsv = async () => {
    if (!hasGridRows) {
      toast.warning("No data to export");
      return;
    }
    if (isExporting) return;
    setIsExporting(true);
    setOpenMenu(null);
    try {
      const rows = await fetchAllMatchingPatients();
      if (rows.length === 0) {
        toast.warning("No data to export");
        return;
      }
      const csvContent = buildLookupCsv(rows);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = lookupCsvFilename();
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (err) {
      console.error("Lookup patient export failed:", err);
      toast.error("Failed to export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!hasGridRows) {
      toast.warning("No data to export");
      return;
    }
    if (isExporting) return;
    setIsExporting(true);
    setOpenMenu(null);

    try {
      const patients = await fetchAllMatchingPatients();
      if (patients.length === 0) {
        toast.warning("No data to export");
        return;
      }
      const { headers, rows } = getLookupExportTableData(patients);
      exportColoredTablePdf({
        filename: lookupPdfFilename(),
        title: "Look up Patient",
        metaLines: [
          `Generated: ${new Date().toLocaleString()}`,
          `User: ${currentUserLabel}`,
          `Total records: ${rows.length}`,
          ...criteriaLines
        ],
        headers,
        rows,
        headerBg: LOOKUP_TABLE_HEADER_BG
      });
      toast.success("PDF exported successfully");
    } catch (err) {
      console.error("Lookup patient PDF export failed:", err);
      toast.error("Failed to export PDF");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!openMenu) return undefined;
    const onMouseDown = (event) => {
      const target = event.target;
      if (titleActionsRef.current?.contains(target)) return;
      if (filtersAnchorRef.current?.contains(target)) return;
      if (target?.closest?.(".app-date-input-portal-root, .react-datepicker-popper")) return;
      setOpenMenu(null);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenu]);

  const handleEmail = () => {
    if (!hasGridRows) {
      toast.warning("No data to email");
      return;
    }
    setShowEmailModal(true);
  };

  const handleSendEmail = async (emailData) => {
    try {
      const rows = await fetchAllMatchingPatients();
      if (rows.length === 0) {
        throw new Error("No data to email");
      }
      const selectedHeaderSet =
        Array.isArray(emailData.selectedColumns) && emailData.selectedColumns.length > 0
          ? new Set(emailData.selectedColumns)
          : null;
      const csvContent = buildLookupCsv(rows, selectedHeaderSet);
      const file = new File([csvContent], lookupCsvFilename(), { type: "text/csv;charset=utf-8;" });
      const userAttachments = Array.isArray(emailData.attachments) ? emailData.attachments : [];
      const attachments = [file, ...userAttachments];
      const res = await sendReportEmail(
        { ...emailData, attachments },
        { event_id: 11 }
      );
      if (!res.success) throw new Error(res.error || res.message || "Failed to send email");
      toast.success("Email queued successfully");
      setShowEmailModal(false);
    } catch (err) {
      console.error("Lookup patient email failed:", err);
      toast.error(err?.message || "Failed to send email");
    }
  };

  useEffect(() => {
    if (tableLoading) return;
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    } else if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage, tableLoading]);

  const handlePageChange = (newPage) => {
    const validatedPage = Math.max(1, Math.min(newPage, totalPages));
    if (validatedPage !== currentPage) {
      setCurrentPage(validatedPage);
    }
  };

  const handlePatientRowClick = (patient) => {
    const patientId = resolvePatientRecordId(patient);
    const practiceIdForRoute = resolvePatientPracticeId(
      patient,
      activePracticeId || effectivePracticeId || (isScopedRole ? scopedPracticeId : formData.practice)
    );
    const path = buildPatientDetailsPath(practiceIdForRoute, patientId);
    if (!path) {
      toast.error("Unable to open patient details. Select a practice or use a patient with a practice assigned.");
      return;
    }
    const program = String(activeResultProgram || PROGRAM_RPM).toUpperCase();
    navigate(path, {
      state: {
        fromDashboard: true,
        activeProgram: program,
        breadcrumbTrail: [
          lookupBaseCrumb,
          { label: "Look up Patient", path: "/patients/lookup" }
        ]
      }
    });
  };

  const formatVitalReading = (raw) => {
    const num = (raw != null && raw !== "N/A" && raw !== "") ? Number(raw) : null;
    return (num != null && !Number.isNaN(num) && num > 0) ? Math.round(num) : "N/A";
  };

  const formatWeightLbs = (patient) => {
    const val = patient.last_weight ?? patient.weight;
    if (val == null || val === "" || val === "N/A" || Number(val) <= 0 || Number.isNaN(Number(val))) return "N/A";
    return String(val);
  };

  const statusForBadge = (patient) =>
    patientStatusLabel(patient?.status ?? patient?.status_id ?? patient?.patient_status);

  const indexColumn = {
    key: "index",
    title: "#",
    sortable: false,
    className: "lookup-patient-index-col reusable-table-centered-col",
    render: (_patient, index) => (currentPage - 1) * apiLimit + index + 1
  };

  const practiceColumn = {
    key: "practice_name",
    title: "PRACTICE NAME",
    sortable: true,
    render: (p) => p.practice_name || p.practice || "N/A"
  };

  const rpmColumns = [
    indexColumn,
    {
      key: "status",
      title: "STATUS",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => <StatusBadge status={statusForBadge(p)} />
    },
    {
      key: "vitals",
      title: "VITALS",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => {
        const value = String(p.vitals || "").trim();
        return value && value !== "-" ? value : "-";
      }
    },
    {
      key: "name",
      title: "NAME",
      sortable: true,
      render: (p) =>
        p.name ||
        `${p.last_name || ""}, ${p.first_name || ""}`.replace(/^,\s*|\s*,$/g, "").trim() ||
        "N/A"
    },
    ...(showPracticeColumn ? [practiceColumn] : []),
    {
      key: "systolic",
      title: "SYSTOLIC",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => {
        const value = formatVitalReading(p.last_systolic ?? p.systolic);
        return renderVitalValue(value, getPatientListVitalStatuses(p).sysStatus);
      }
    },
    {
      key: "diastolic",
      title: "DIASTOLIC",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => {
        const value = formatVitalReading(p.last_diastolic ?? p.diastolic);
        return renderVitalValue(value, getPatientListVitalStatuses(p).diaStatus);
      }
    },
    {
      key: "pulse",
      title: "PULSE",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => {
        const value = formatVitalReading(p.last_pulse ?? p.pulse);
        return renderPulseValue(value, getPatientListVitalStatuses(p).pulseStatus);
      }
    },
    {
      key: "glucose",
      title: "GLUCOSE",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => {
        const value = formatVitalReading(p.last_glucose ?? p.glucose);
        return renderVitalValue(value, getPatientListVitalStatuses(p).glucoseStatus);
      }
    },
    {
      key: "weight",
      title: "WEIGHT",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => {
        const value = formatWeightLbs(p);
        return renderVitalValue(value, getPatientListVitalStatuses(p).weightStatus);
      }
    },
    {
      key: "last_upload",
      title: "LAST UPLOAD",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => formatDateOnlyUs(p.last_upload_date) || "N/A"
    },
    {
      key: "readings",
      title: "# READINGS",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => p.readings_count ?? p.number_of_readings ?? "0"
    },
    {
      key: "service_time",
      title: "TIME",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => p.service_time ?? "0"
    }
  ];

  const ccmColumns = [
    indexColumn,
    {
      key: "status",
      title: "STATUS",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => <StatusBadge status={statusForBadge(p)} />
    },
    {
      key: "name",
      title: "PATIENT NAME",
      sortable: true,
      render: (p) =>
        p.patient_name ||
        `${p.last_name || ""}, ${p.first_name || ""}`.replace(/^,\s*|\s*,$/g, "").trim() ||
        "N/A"
    },
    ...(showPracticeColumn ? [practiceColumn] : []),
    {
      key: "dob",
      title: "DOB",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => formatDateOnlyUs(p.date_of_birth) || "N/A"
    },
    {
      key: "chronic_conditions",
      title: "CHRONIC CONDITIONS",
      sortable: true,
      render: (p) => formatChronicDiseaseNames(p.chronic_conditions || p.icd_codes)
    },
    {
      key: "last_activity",
      title: "LAST ACTIVITY",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) =>
        p.last_activity_date
          ? formatDateOnlyUs(p.last_activity_date) || new Date(p.last_activity_date).toLocaleDateString()
          : "N/A"
    },
    {
      key: "service_time",
      title: "SERVICE TIME",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => p.service_time ?? "0"
    },
    {
      key: "billing",
      title: "BILLING",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (p) => p.billing_status || "Pending"
    }
  ];

  const columns = activeResultProgram === PROGRAM_RPM ? rpmColumns : ccmColumns;

  const emptyMessage = hasQueryAttempted
    ? "No patients match these filters"
    : systemCaregiverMultiPractice && !formData.practice
      ? "Select a practice to load patients"
      : "Loading patients...";

  const personName = (list, id) => {
    const row = (list || []).find((x) => String(x.id) === String(id));
    if (!row) return String(id);
    return (
      `${row.last_name || ""}, ${row.first_name || ""}`.replace(/^,\s*|\s*,$/g, "").trim() ||
      row.name ||
      row.username ||
      String(id)
    );
  };

  const filterTags = [];
  if (appliedFilters.status) {
    filterTags.push({
      keys: ["status"],
      label: `Status: ${statusOptions.find((s) => s.value === String(appliedFilters.status))?.label || appliedFilters.status}`
    });
  }
  if (appliedFilters.provider) {
    filterTags.push({ keys: ["provider"], label: `Provider: ${personName(providers, appliedFilters.provider)}` });
  }
  if (appliedFilters.lastName) filterTags.push({ keys: ["lastName"], label: `Last name: ${appliedFilters.lastName}` });
  if (appliedFilters.firstName) filterTags.push({ keys: ["firstName"], label: `First name: ${appliedFilters.firstName}` });
  if (appliedFilters.phone) {
    filterTags.push({
      keys: ["phone"],
      label: `Phone: ${formatPhoneForDisplay(appliedFilters.phone) || appliedFilters.phone}`
    });
  }
  if (appliedFilters.dobOperator && appliedFilters.dobFrom) {
    const opLabel = DOB_OPERATORS.find((o) => o.value === appliedFilters.dobOperator)?.label || "";
    const from = formatDateOnlyUs(appliedFilters.dobFrom) || appliedFilters.dobFrom;
    const to = appliedFilters.dobTo ? formatDateOnlyUs(appliedFilters.dobTo) || appliedFilters.dobTo : "";
    filterTags.push({
      keys: ["dobOperator", "dobFrom", "dobTo"],
      label: `DOB: ${opLabel} ${from}${to ? ` – ${to}` : ""}`
    });
  }
  if (appliedFilters.caregiver) {
    filterTags.push({ keys: ["caregiver"], label: `Caregiver: ${personName(caregivers, appliedFilters.caregiver)}` });
  }
  if (appliedFilters.systemCaregiver) {
    filterTags.push({
      keys: ["systemCaregiver"],
      label: `System caregiver: ${personName(systemCaregivers, appliedFilters.systemCaregiver)}`
    });
  }
  if (appliedFilters.vitals) {
    const vitalLabels = String(appliedFilters.vitals)
      .split(",")
      .filter(Boolean)
      .map((id) => vitalsOptions.find((v) => v.value === id)?.label || id)
      .join(", ");
    filterTags.push({ keys: ["vitals"], label: `Vitals: ${vitalLabels}` });
  }
  if (appliedFilters.serialNumber) {
    filterTags.push({ keys: ["serialNumber"], label: `Serial #: ${appliedFilters.serialNumber}` });
  }
  if (appliedFilters.rpmStartFrom) {
    filterTags.push({
      keys: ["rpmStartFrom"],
      label: `Service from: ${formatDateOnlyUs(appliedFilters.rpmStartFrom) || appliedFilters.rpmStartFrom}`
    });
  }
  if (appliedFilters.rpmStartTo) {
    filterTags.push({
      keys: ["rpmStartTo"],
      label: `Service to: ${formatDateOnlyUs(appliedFilters.rpmStartTo) || appliedFilters.rpmStartTo}`
    });
  }

  const panelFilterCount = PANEL_FILTER_KEYS.filter((k) => {
    const v = appliedFilters[k];
    return Array.isArray(v) ? v.length > 0 : Boolean(v);
  }).length;

  const clearAllFilterTags = () => {
    setAndApply({ ...emptyPanelFilters(), status: "", provider: "" });
    setIsVitalsDropdownOpen(false);
  };

  const showProgramChips = hasMultiplePrograms;
  const countLabel =
    hasQueryAttempted && !tableLoading
      ? `· ${totalPatients} patient${totalPatients === 1 ? "" : "s"}`
      : "";

  return (
    <div className="list-screen-page-container lookup-patient-page">
      <div className="list-screen-top-bar lkp-page-head">
            <div className="list-screen-title-section">
              <h2 className="dashboard-title list-screen-title-text">
                Look up Patient
                {countLabel ? <span className="lkp-count">{countLabel}</span> : null}
              </h2>
            </div>
            <div className="lkp-title-actions" ref={titleActionsRef}>
              <button
                type="button"
                className="lkp-chip"
                disabled={!hasGridRows || isExporting}
                aria-haspopup="menu"
                aria-expanded={openMenu === "export"}
                onClick={() => setOpenMenu((m) => (m === "export" ? null : "export"))}
              >
                {isExporting ? "Exporting..." : "Export"}
                <svg className="lkp-chev" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                className="lkp-chip lkp-chip-icon"
                aria-label="More actions"
                title="More actions"
                aria-haspopup="menu"
                aria-expanded={openMenu === "more"}
                onClick={() => setOpenMenu((m) => (m === "more" ? null : "more"))}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <circle cx="5" cy="12" r="1.8" />
                  <circle cx="12" cy="12" r="1.8" />
                  <circle cx="19" cy="12" r="1.8" />
                </svg>
              </button>

              {openMenu === "export" && (
                <div className="lkp-popover lkp-menu" role="menu">
                  <button type="button" role="menuitem" className="lkp-menu-item" onClick={handleExportPdf} disabled={isExporting}>
                    Export PDF
                  </button>
                  <button type="button" role="menuitem" className="lkp-menu-item" onClick={handleExportCsv} disabled={isExporting}>
                    Export CSV
                  </button>
                </div>
              )}
              {openMenu === "more" && (
                <div className="lkp-popover lkp-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="lkp-menu-item"
                    disabled={!hasGridRows}
                    onClick={() => {
                      setOpenMenu(null);
                      handlePrint();
                    }}
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="lkp-menu-item"
                    disabled={!hasGridRows}
                    onClick={() => {
                      setOpenMenu(null);
                      handleEmail();
                    }}
                  >
                    Email
                  </button>
                </div>
              )}
            </div>
      </div>

      <section className="lkp-card">
        <div className="lkp-card-head">
          {/* Row 1: search + main filters */}
          <div className="lkp-row lkp-toolbar-row" ref={filtersAnchorRef}>
            <label className="lkp-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                type="search"
                id="lookup-patient-search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search name or serial number"
                aria-label="Search patients by name or serial number"
              />
            </label>

            {showProgramChips && (
              <div className="lkp-chip-group" role="tablist" aria-label="Program enrolled">
                {availablePrograms.map((program) => (
                  <button
                    key={program}
                    type="button"
                    role="tab"
                    aria-selected={activeResultProgram === program}
                    className={`lkp-chip${activeResultProgram === program ? " active" : ""}`}
                    disabled={programsLoading}
                    onClick={() => handleProgramChipChange(program)}
                  >
                    {program.toUpperCase()}
                  </button>
                ))}
              </div>
            )}

            <select
              className="lkp-chip lkp-select"
              aria-label="Status"
              value={formData.status}
              onChange={(e) => setAndApply({ status: e.target.value })}
            >
              <option value="">All statuses</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {showPracticeField && (
              <select
                name="practice"
                className="lkp-chip lkp-select lkp-select-wide"
                aria-label="Practice"
                value={formData.practice}
                onChange={handleChange}
                disabled={loadingDropdowns}
              >
                <option value="">All practices</option>
                {Array.isArray(practiceDropdownList) &&
                  practiceDropdownList.map((practice, index) => (
                    <option key={practice.id || `practice-${index}`} value={practice.id}>
                      {practice.practice_name || practice.name || "Unnamed Practice"}
                    </option>
                  ))}
              </select>
            )}

            {showProviderFilter && (
            <select
              className="lkp-chip lkp-select lkp-select-wide"
              aria-label="Provider"
              value={formData.provider}
              onChange={(e) => setAndApply({ provider: e.target.value })}
              disabled={!effectivePracticeId || effectivePracticeId === "all" || loadingDropdowns}
              title={!effectivePracticeId || effectivePracticeId === "all" ? "Select a practice first" : undefined}
            >
              <option value="">All providers</option>
              {Array.isArray(providers) &&
                providers.map((provider, index) => (
                  <option key={provider.id || `provider-${index}`} value={provider.id}>
                    {provider.last_name}, {provider.first_name}
                  </option>
                ))}
            </select>
            )}

            <button
              type="button"
              className={`lkp-chip${openMenu === "filters" ? " open" : ""}`}
              aria-expanded={openMenu === "filters"}
              onClick={() => setOpenMenu((m) => (m === "filters" ? null : "filters"))}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="7" y1="12" x2="17" y2="12" />
                <line x1="10" y1="18" x2="14" y2="18" />
              </svg>
              More filters
              {panelFilterCount > 0 && <span className="lkp-filter-badge">{panelFilterCount}</span>}
            </button>

            {openMenu === "filters" && (
              <div className="lkp-popover lkp-filters-panel" role="dialog" aria-label="More filters">
                <div className="lkp-filters-grid">
                  <label className="lkp-field">
                    <span>Last name</span>
                    <input name="lastName" value={formData.lastName} onChange={handleChange} className="lkp-input" type="text" />
                  </label>
                  <label className="lkp-field">
                    <span>First name</span>
                    <input name="firstName" value={formData.firstName} onChange={handleChange} className="lkp-input" type="text" />
                  </label>
                  <div className="lkp-field">
                    <span>Phone number</span>
                    <PhoneNumberInput
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className="lkp-input"
                      placeholder="(000) 000-0000"
                    />
                  </div>
                  <label className="lkp-field">
                    <span>DOB</span>
                    <select name="dobOperator" value={formData.dobOperator} onChange={handleChange} className="lkp-input">
                      {DOB_OPERATORS.map((option) => (
                        <option key={option.value || "none"} value={option.value}>
                          {option.value ? option.label : "Any date"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="lkp-field">
                    <span>{showDobTo ? "DOB (from)" : "Date of birth"}</span>
                    <AppDateInput
                      name="dobFrom"
                      value={formData.dobFrom}
                      onChange={handleDobFromChange}
                      className="lkp-input"
                      max={todayIso}
                      disabled={!formData.dobOperator}
                      showCalendarIcon
                    />
                  </div>
                  {showDobTo && (
                    <div className="lkp-field">
                      <span>DOB (to)</span>
                      <AppDateInput
                        name="dobTo"
                        value={formData.dobTo}
                        onChange={handleDobToChange}
                        className="lkp-input"
                        min={formData.dobFrom || undefined}
                        max={todayIso}
                        showCalendarIcon
                      />
                    </div>
                  )}
                  {showCaregiverFilter && (
                  <label className="lkp-field">
                    <span>Caregiver</span>
                    <select
                      name="caregiver"
                      value={formData.caregiver}
                      onChange={handleChange}
                      className="lkp-input"
                      disabled={!effectivePracticeId || effectivePracticeId === "all" || loadingDropdowns}
                    >
                      <option value="">All caregivers</option>
                      {Array.isArray(caregivers) &&
                        caregivers.map((caregiver, index) => (
                          <option key={caregiver.id || `caregiver-${index}`} value={caregiver.id}>
                            {caregiver.last_name}, {caregiver.first_name}
                          </option>
                        ))}
                    </select>
                  </label>
                  )}
                  {!isScopedRole && (
                    <label className="lkp-field">
                      <span>System caregiver</span>
                      <select
                        name="systemCaregiver"
                        value={formData.systemCaregiver}
                        onChange={handleChange}
                        className="lkp-input"
                        disabled={loadingDropdowns}
                      >
                        <option value="">All system caregivers</option>
                        {Array.isArray(systemCaregivers) &&
                          systemCaregivers.map((sc, index) => (
                            <option key={sc.id || `sc-${index}`} value={sc.id}>
                              {sc.last_name || sc.first_name
                                ? `${sc.last_name || ""}, ${sc.first_name || ""}`.replace(/^,\s*|\s*,$/g, "").trim()
                                : sc.name || sc.username || `System Caregiver #${sc.id}`}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  {showRpmFilters && (
                    <>
                      <div className="lkp-field">
                        <span>Vitals</span>
                        <div className="lookup-patient-multiselect" ref={vitalsDropdownRef}>
                          <button
                            type="button"
                            className="lookup-patient-multiselect-trigger lkp-input"
                            onClick={() => setIsVitalsDropdownOpen((prev) => !prev)}
                          >
                            <span>
                              {Array.isArray(formData.vitals) && formData.vitals.length > 0
                                ? vitalsOptions
                                    .filter((option) => formData.vitals.includes(option.value))
                                    .map((option) => option.label)
                                    .join(", ")
                                : "All vitals"}
                            </span>
                            <span className="lookup-patient-multiselect-arrow">{isVitalsDropdownOpen ? "▲" : "▼"}</span>
                          </button>
                          {isVitalsDropdownOpen && (
                            <div className="lookup-patient-multiselect-menu">
                              {vitalsOptions.map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  className={`lookup-patient-multiselect-option${
                                    Array.isArray(formData.vitals) && formData.vitals.includes(option.value) ? " selected" : ""
                                  }`}
                                  onClick={() => toggleVitalOption(option.value)}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <label className="lkp-field">
                        <span>Serial #</span>
                        <input
                          name="serialNumber"
                          value={formData.serialNumber}
                          onChange={handleChange}
                          className="lkp-input"
                          type="text"
                          maxLength={DEVICE_SERIAL_NUMBER_LENGTH}
                          placeholder="16 characters"
                          aria-label="Serial number, up to 16 alphanumeric characters"
                        />
                      </label>
                    </>
                  )}
                </div>
                <div className="lkp-filters-foot">
                  <button type="button" className="lkp-link-btn" onClick={handleClearMoreFilters}>
                    Clear
                  </button>
                  <button
                    type="button"
                    className="lkp-chip lkp-chip-solid"
                    onClick={handleApplyMoreFilters}
                    disabled={tableLoading || programsLoading}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Active filter tags */}
          {filterTags.length > 0 && (
            <div className="lkp-row lkp-tags-row">
              <div className="lkp-tags">
                {filterTags.map((tag) => (
                  <span className="lkp-tag" key={tag.keys.join("-")}>
                    {tag.label}
                    <button type="button" aria-label={`Remove ${tag.label}`} onClick={() => handleRemoveFilterTag(tag.keys)}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <button type="button" className="lkp-link-btn" onClick={clearAllFilterTags}>
                Clear all
              </button>
            </div>
          )}
        </div>

        <div className="lookup-patient-print-meta" aria-hidden="true">
          <h2>Look up Patient</h2>
          <div className="lookup-patient-print-meta-row">
            <span>Generated: {printTimestamp}</span>
            <span>User: {currentUserLabel}</span>
          </div>
          <div className="lookup-patient-print-criteria">
            {criteriaLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </div>

        {/* Patient table */}
        <div className="lkp-table-block lookup-patient-table-section">
          <div className="lookup-patient-table-wrapper">
            <Table
              columns={columns}
              data={sortedAndFilteredPatients}
              sortBy={sortConfig.key}
              sortOrder={sortConfig.direction}
              onSort={requestSort}
              isLoading={tableLoading || !hasQueryAttempted}
              loadingMode="skeleton"
              skeletonRows={8}
              emptyMessage={emptyMessage}
              onRowClick={handlePatientRowClick}
              getRowKey={(patient, index) =>
                `${resolvePatientPracticeId(patient, activePracticeId) || "p"}-${resolvePatientRecordId(patient) || index}`
              }
            />

            {totalPatients > 0 && (
              <ListScreenPaginationBar
                entriesValue={String(entriesPerPage)}
                onEntriesChange={(value) => {
                  const nextEntries = value === "all" ? "all" : String(value);
                  setEntriesPerPage(nextEntries);
                  setCurrentPage(1);
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </section>

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        availableColumns={availableEmailColumns}
        onSend={handleSendEmail}
      />

      <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        availableColumns={availableEmailColumns}
        onPrint={handlePerformPrint}
        title="Print Look up Patient"
      />
    </div>
  );
};

export default LookupPatient;
